import { useState, useEffect } from "react";
import { Layout, Badge, Dropdown, List, Avatar } from "antd";
import { MenuOutlined, BellOutlined } from "@ant-design/icons";
import Sidebar from "../main-layout/Sidebar";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import { ProtectedSharedRoutes } from "../../shared/routes/ProtectedInnerRoutes";
import { SelfUserClassificationEnum } from "../../shared/constants/constants";
import { navigateToAuth } from "../../utils/helper/redirect";
import useAutoLogout from "../../shared/common/useAutoLogout";
import {
  useDashboardLayout,
  DASHBOARD_LAYOUTS,
} from "../../store/context/dashboard-layout-context";
import { useTenant } from "../../store/context/tenant-context";

import { getGreeting } from "../../utils/helper/getGreeting";
import { darkenHex } from "../../utils/helper/colorUtils";
import DashboardLayoutSwitcher from "../../components/Dashboard/DashboardLayoutSwitcher";
import { loadNotificationsFromSession, normalizeNotification } from "../../utils/helper/util";
import { postAuthenticatedRequest } from "../../utils/api/requests/request";

const { Content, Sider } = Layout;

const InnerLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionData = getSessionData();

  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [notificationList, setNotificationList] = useState([]);

  useEffect(() => {
    if (!sessionData?.userId) return;

    // Seed with locally cached (WebSocket-pushed) notifications immediately
    const sessionNotifs = loadNotificationsFromSession(sessionData.userId);
    setNotificationList(sessionNotifs);

    // Fetch server notifications (pending authorisations, alerts, etc.)
    postAuthenticatedRequest("GetUserNotificationsQuery", { target: "current", limit: 50 })
      .then((res) => {
        if (!res?.data?.isSuccessful) return;
        const serverNotifs = (res.data?.data?.notifications ?? []).map(normalizeNotification);
        // Merge: server first, then session extras — dedup by id
        setNotificationList((prev) => {
          const seen = new Set(serverNotifs.map((n) => n.id));
          const extras = prev.filter((n) => !seen.has(n.id));
          return [...serverNotifs, ...extras];
        });
      })
      .catch(() => {}); // silently ignore — session cache is the fallback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData?.userId]);

  const { layout, reloadPreferences } = useDashboardLayout();
  const tenant = useTenant();

  // Re-load server preferences every time an authenticated user lands here
  // (covers fresh login where the initial context mount had no session yet)
  useEffect(() => {
    reloadPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const primary = tenant?.theme?.colorPrimary || "#0b3d2e";

  const SIDER_BG = {
    [DASHBOARD_LAYOUTS.MODERN_GRADIENT]: `linear-gradient(180deg, ${darkenHex(primary, 0.75)} 0%, ${primary} 100%)`,
    [DASHBOARD_LAYOUTS.GLASSMORPHISM]: "#111827",
  };
  const siderBackground = SIDER_BG[layout] ?? undefined;

  useAutoLogout();

  const isAuthenticated = !!sessionData; // Check if user is logged in

  console.log("sessionData from inner layout", sessionData);
  const isAdmin =
    sessionData?.userClassification ===
    SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value; // Check if user is admin

  // Redirect to login if user is not authenticated
  if (!isAuthenticated) {
    navigateToAuth({ isAdmin: false });
  }

  const isSharedRoute = ProtectedSharedRoutes.some(
    (route) => route.path === location.pathname
  ); // Check if the route is a shared route between authenticated users and admin

  // Public routes that use InnerLayout for authenticated users should always be allowed
  const alwaysAllowedPrefixes = ['/request-approval/'];
  const isAlwaysAllowed = alwaysAllowedPrefixes.some(
    (prefix) => location.pathname.startsWith(prefix)
  );

  // If the user is an admin and trying to access non-shared user route, redirect to admin dashboard
  if (isAdmin && !isSharedRoute && !isAlwaysAllowed) {
    return <Navigate to="/admin-dashboard" replace />;
  }

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const handleResize = () => {
    const isSmall = window.innerWidth < 768;
    setIsSmallScreen(isSmall);
    setCollapsed(isSmall);
  };

  return (
    <Layout hasSider>
      {/* Sidebar Sider */}
      <Sider
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={270}
        collapsedWidth={0}
        style={{
          overflow: "hidden",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          transition: collapsed ? "left 0.3s ease" : "none",
          ...(siderBackground && { background: siderBackground }),
        }}
        breakpoint="md"
        onBreakpoint={(broken) => {
          setCollapsed(broken);
        }}
      >
        <Sidebar collapsed={collapsed} type="inner" />
      </Sider>

      {/* MenuOutlined icon for toggling sidebar on small screens */}
      {collapsed && isSmallScreen && (
        <MenuOutlined
          style={{
            position: "fixed",
            top: 20,
            left: 20,
            zIndex: 120,
            cursor: "pointer",
          }}
          onClick={toggleCollapsed}
        />
      )}

      {/* Backdrop */}
      {!collapsed && isSmallScreen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 90,
          }}
          onClick={toggleCollapsed}
        />
      )}

      {/* Main Layout */}
      <Layout
        style={{
          marginLeft: isSmallScreen ? 0 : 270,
          transition: "margin-left 0.2s ease",
        }}
      >
        {/* ── Universal page header: greeting + controls on ONE row ── */}
        {(() => {
          const isGlass = layout === DASHBOARD_LAYOUTS.GLASSMORPHISM;
          const textColor = isGlass ? "#fff" : "#111";
          const subColor = isGlass ? "rgba(255,255,255,0.4)" : "#aaa";
          const bg = isGlass ? "rgba(17,24,39,0.95)" : "#fff";
          const border = isGlass ? "1px solid rgba(255,255,255,0.06)" : "1px solid #ebebeb";
          const bellBg = isGlass ? "rgba(255,255,255,0.08)" : "#f5f5f5";
          const bellBorder = isGlass ? "1px solid rgba(255,255,255,0.15)" : "1px solid #e5e5e5";
          const bellColor = isGlass ? "rgba(255,255,255,0.7)" : "#555";
          const notificationMenu = (
            <div style={{ width: 300, background: "#fff", borderRadius: 10, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", overflow: "hidden" }}>
              {notificationList.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center", color: "#999", fontSize: 13 }}>No new notifications</div>
              ) : (
                <List size="small" dataSource={notificationList} renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<BellOutlined style={{ fontSize: 13 }} />} size={30} />}
                      title={<span style={{ fontSize: 12 }}>{item.message}</span>}
                    />
                  </List.Item>
                )} />
              )}
            </div>
          );
          return (
            <div style={{ padding: "10px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", background: bg, borderBottom: border }}>
              <div>
                <p style={{ fontSize: 17, fontWeight: 700, color: textColor, margin: 0 }}>
                  {getGreeting()},{" "}
                  <span style={{ color: primary, textTransform: "capitalize" }}>{sessionData?.firstName}</span>
                </p>
                <p style={{ fontSize: 12, color: subColor, margin: 0 }}>
                  {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <DashboardLayoutSwitcher />
                <Dropdown overlay={notificationMenu} trigger={["click"]}>
                  <Badge count={notificationList.length} offset={[4, 0]}>
                    <button style={{ width: 34, height: 34, borderRadius: 9, border: bellBorder, background: bellBg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <BellOutlined style={{ fontSize: 15, color: bellColor }} />
                    </button>
                  </Badge>
                </Dropdown>
              </div>
            </div>
          );
        })()}

        {/* Main Content */}
        <Content
          style={{
            overflow: "auto",
            padding: "24px",
          }}
        >
          <div className="min-h-[100vh] h-auto">{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default InnerLayout;
